import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PageWrapper from "../PageWrapper/PageWrapper";
import 'react-datepicker/dist/react-datepicker.css';
import { useState } from "react";
import { Contract } from "../../api/ContractsAPI";
import Bubbles from "../../components/Bubbles/Bubbles";
import { useContractTemplate } from "../../hooks/useContractTemplate";
import SelectInput from "../../components/SelectInput/SelectInput";
import SecondaryButton from "../../components/SecondaryButton/SecondaryButton";
import { CardV2, CardContent, CardHeader, CardTitle } from "../../components/CardV2/CardV2";
import { FileText, Globe, ArrowLeft } from "lucide-react";
import ContractsAPI from "../../api/ContractsAPI";
import { toastService } from "../../services/ToastService";
import ContractTemplateEditor from "../../components/ContractTemplateEditor/ContractTemplateEditor";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";

const EMPTY_CONTRACT: Contract = {
  signatureDay: new Date().toISOString(), // YYYY-MM-DDTHH:mm:ss.sssZ
  clientFullname: '',
  clientPersonalID: '',
  clientAddress: '',
  clientPhoneNumber: '',
  clientEmail: '',
  dress: '',
  dressTrain: '',
  dressVeil: '',
  dressVeilLength: 0,
  dressDescription: '',
  dressLegCut: '',
  weddingDate: new Date().toISOString(), // YYYY-MM-DDTHH:mm:ss.sssZ
  bust: '',
  waist: '',
  hips: '',
  totalPrice: 0,
  totalPriceWording: '',
  totalPriceCurrency: '',
  instalments: [],
  endDate: new Date().toISOString(), // YYYY-MM-DDTHH:mm:ss.sssZ
  clientPickUpDate: new Date().toISOString(), // YYYY-MM-DDTHH:mm:ss.sssZ
  employeeName: '',
  language: 'PL_PL',
  firstFollowUpMeetingDate: new Date().toISOString(), // YYYY-MM-DDTHH:mm:ss.sssZ
}

function ContractsPage() {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contractTemplate, updateContractTemplate, setLocale, locale } = useContractTemplate();
  const [contract, setContract] = useState<Contract>(() => ({ ...EMPTY_CONTRACT, language: locale }));
  const [isEditing, ] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const handleLanguageChange = (newLanguage: string) => {
    setLocale(newLanguage);
    setContract(prev => ({...prev, language: newLanguage }));
    toastService.showToast(t("contracts.language_changed") || `Language changed to ${newLanguage}`, "success");
  }

  const handleGenerateTestPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const testContract: Contract = {
        ...EMPTY_CONTRACT,
        language: contract.language,
        clientFullname: 'Test Client',
        clientPersonalID: 'TEST123456',
        clientAddress: '123 Test Street, Test City',
        clientPhoneNumber: '+1234567890',
        clientEmail: 'test@example.com',
        dress: 'Test Dress',
        dressTrain: '',
        dressVeil: 'Yes',
        dressVeilLength: 50,
        dressDescription: 'Beautiful test dress for demonstration',
        dressLegCut: '',
        weddingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        bust: '90cm',
        waist: '70cm',
        hips: '95cm',
        totalPrice: 1500,
        totalPriceWording: 'One thousand five hundred',
        totalPriceCurrency: 'EUR',
        instalments: [
          { amount: 500, dueDate: new Date().toISOString(), method: 'Card', amountInWords: 'Five hundred' },
          { amount: 1000, dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(), method: 'Transfer', amountInWords: 'One thousand' }
        ],
        endDate: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000).toISOString(), // 25 days from now
        clientPickUpDate: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString(), // 28 days from now
        employeeName: 'Test Employee',
        firstFollowUpMeetingDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
      };

      const response = await ContractsAPI.preview(testContract);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      
      window.open(url, "_blank");
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `test-contract-${contract.language.toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      toastService.showToast(t("contracts.test_pdf_success") || "Test contract PDF generated successfully!", "success");
    } catch (error) {
      console.error('Error generating test PDF:', error);
      toastService.showToast(t("contracts.test_pdf_error") || "Failed to generate test contract PDF", "error");
    } finally {
      setIsGeneratingPDF(false);
    }
  }


  return (
    <PageWrapper>
      <>
        <div className="mb-4">
          <PrimaryButton
            text={<span className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              {t("contracts.go_back_to_sales") || "Go back to Sales"}
            </span>}
            fn={() => navigate("/sales")}
            actionName="Go back to Sales"
          />
        </div>
        <Bubbles count={10} />
        <CardV2 className="mb-6">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {t("contract_template.title") || "Contract Template"}
              </CardTitle>

              <SecondaryButton
                text={
                  <span className="flex items-center gap-2">
                    {isGeneratingPDF ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
                        {t("contracts.generating") || "Generating..."}
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4" />
                        {t("contracts.generate_test") || "Generate Test PDF"}
                      </>
                    )}
                  </span>
                }
                fn={handleGenerateTestPDF}
                actionName="Generate Test Contract PDF"
                isDisabled={isGeneratingPDF}
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Globe className="w-4 h-4 text-gray-600 flex-shrink-0" />
              <div className="min-w-[150px]">
                <SelectInput
                  values={[
                    { value: 'EN_US', readableValue: 'global.english'},
                    { value: 'PL_PL', readableValue: 'global.polish'},
                    { value: 'PT_PT', readableValue: 'global.portuguese'},
                    { value: 'ES_ES', readableValue: 'global.spanish'},
                    { value: 'IT_IT', readableValue: 'global.italian'},
                    { value: 'DE_DE', readableValue: 'global.german'},
                  ]}
                  defaultValue={locale}
                  onChange={e => handleLanguageChange(e.target.value)}
                  label=""
                />
              </div>
            </div>
          </CardContent>
        </CardV2>

        {isEditing ? (
          <ContractTemplateEditor
            contractTemplate={contractTemplate}
            updateContractTemplate={updateContractTemplate}
            selectedLocale={locale}
          />
        ) : (
          // ContractDisplay temporarily hidden
          null
          // <ContractDisplay
          //   contract={contract}
          //   template={contractTemplate}
          //   setContract={setContract}
          //   reset={resetContract}
          // />
        )}
      </>
    </PageWrapper>
  );
}

export default ContractsPage;
